/**
 * CPF e CNPJ: validar e formatar.
 *
 * Um recibo que traz `CPF 111.111.111-11` no lugar do número certo é pior que um
 * recibo sem CPF: o erro está assinado. Por isso o número é **conferido**, não
 * apenas guardado — e a conta é a de sempre, a dos dígitos verificadores, feita
 * aqui e não só na tela (a tela é conveniência; o servidor é a garantia).
 *
 * ## O que é conferido
 *
 * - **CPF**: 11 dígitos, não todos iguais, e os dois dígitos verificadores pelo
 *   módulo 11. `111.111.111-11` e `123.456.789-00` são recusados.
 * - **CNPJ**: 14 dígitos, não todos iguais, e os dois verificadores pelo módulo 11
 *   com os pesos 2..9.
 * - **CNPJ alfanumérico**: a Receita passou a emitir, a partir de julho de 2026,
 *   CNPJ com letras nas doze primeiras posições (`12.ABC.345/01DE-35`). Aqui ele
 *   também é aceito: nas letras, o valor usado na conta é o código ASCII menos 48.
 *   Um validador que só entende dígitos recusaria CNPJ novo e legítimo — e o
 *   cliente não teria como saber por quê.
 */
/* ------------------------------------------------------------------ */
/* Só os dígitos                                                      */
/* ------------------------------------------------------------------ */
/** Tira ponto, traço, barra e espaço — e devolve letras maiúsculas quando houver. */
export function limparDocumento(texto) {
    return texto
        .replace(/[\s.\-/]/g, '')
        .trim()
        .toUpperCase();
}
/** Quantos caracteres úteis o número tem (para separar CPF de CNPJ). */
export function tamanhoDoDocumento(texto) {
    return limparDocumento(texto).length;
}
/* ------------------------------------------------------------------ */
/* CPF                                                                */
/* ------------------------------------------------------------------ */
function digitosVerificadoresCpf(base) {
    const calcular = (numeros) => {
        const soma = numeros.reduce((total, numero, indice) => total + numero * (numeros.length + 1 - indice), 0);
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };
    const numeros = base.split('').map(Number);
    const primeiro = calcular(numeros);
    const segundo = calcular([...numeros, primeiro]);
    return `${primeiro}${segundo}`;
}
/** `529.982.247-25` é válido. */
export function validarCpf(texto) {
    const cpf = limparDocumento(texto);
    if (!/^\d{11}$/.test(cpf))
        return false;
    /* Número repetido passa na conta mas não existe: 000.000.000-00, 111… */
    if (/^(\d)\1{10}$/.test(cpf))
        return false;
    return cpf.slice(9) === digitosVerificadoresCpf(cpf.slice(0, 9));
}
/** `52998224725` → `529.982.247-25`. Texto que não tem 11 dígitos volta como veio. */
export function formatarCpf(texto) {
    const cpf = limparDocumento(texto);
    if (!/^\d{11}$/.test(cpf))
        return texto.trim();
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}
/* ------------------------------------------------------------------ */
/* CNPJ                                                               */
/* ------------------------------------------------------------------ */
/** O valor de um caractere na conta: dígito é o próprio número, letra é ASCII − 48. */
function valorDoCaractere(caractere) {
    return caractere.charCodeAt(0) - 48;
}
function digitosVerificadoresCnpj(base) {
    const calcular = (numeros) => {
        let peso = 2;
        let soma = 0;
        /* Os pesos correm da direita para a esquerda: 2, 3, …, 9, 2, 3, … */
        for (let indice = numeros.length - 1; indice >= 0; indice -= 1) {
            soma += numeros[indice] * peso;
            peso = peso === 9 ? 2 : peso + 1;
        }
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };
    const numeros = base.split('').map(valorDoCaractere);
    const primeiro = calcular(numeros);
    const segundo = calcular([...numeros, primeiro]);
    return `${primeiro}${segundo}`;
}
/**
 * `11.222.333/0001-81` é válido — e `12.ABC.345/01DE-35` (alfanumérico) também.
 */
export function validarCnpj(texto) {
    const cnpj = limparDocumento(texto);
    if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj))
        return false;
    if (/^(\d)\1{13}$/.test(cnpj))
        return false;
    return cnpj.slice(12) === digitosVerificadoresCnpj(cnpj.slice(0, 12));
}
/** `11222333000181` → `11.222.333/0001-81`. Texto fora do formato volta como veio. */
export function formatarCnpj(texto) {
    const cnpj = limparDocumento(texto);
    if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpj))
        return texto.trim();
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}
/* ------------------------------------------------------------------ */
/* Os dois juntos                                                     */
/* ------------------------------------------------------------------ */
export const TIPOS_DE_DOCUMENTO = ['CPF', 'CNPJ'];
export const ROTULO_DA_PESSOA = {
    FISICA: 'Pessoa física',
    JURIDICA: 'Pessoa jurídica',
};
export const DOCUMENTO_DA_PESSOA = {
    FISICA: 'CPF',
    JURIDICA: 'CNPJ',
};
/**
 * Confere o número e diz **o que ele é** — sem perguntar antes.
 *
 * O tamanho separa os dois: 11 posições só pode ser CPF, 14 só pode ser CNPJ. Isso
 * evita o pior caso do formulário, em que a pessoa escolhe "pessoa jurídica" e
 * digita o CPF do sócio.
 */
export function conferirDocumento(texto) {
    const limpo = limparDocumento(texto);
    if (!limpo)
        return null;
    if (limpo.length === 11) {
        return { tipo: 'CPF', formatado: formatarCpf(limpo), valido: validarCpf(limpo) };
    }
    if (limpo.length === 14) {
        return { tipo: 'CNPJ', formatado: formatarCnpj(limpo), valido: validarCnpj(limpo) };
    }
    return null;
}
/**
 * A mensagem do que está errado — ou `null` quando está tudo certo.
 *
 * A mensagem diz **o que fazer**, não só que errou: "o número é um CNPJ" resolve;
 * "documento inválido" não resolve.
 */
export function problemaDoDocumento(texto, pessoaEsperada) {
    const limpo = limparDocumento(texto);
    if (!limpo)
        return 'Informe o documento.';
    const conferido = conferirDocumento(limpo);
    if (!conferido) {
        return limpo.length < 11
            ? 'Faltam dígitos: o CPF tem 11 e o CNPJ tem 14.'
            : 'Documento com tamanho inválido: o CPF tem 11 dígitos e o CNPJ, 14.';
    }
    if (pessoaEsperada) {
        const esperado = DOCUMENTO_DA_PESSOA[pessoaEsperada];
        if (conferido.tipo !== esperado) {
            return `O número informado é um ${conferido.tipo}, mas você marcou ${ROTULO_DA_PESSOA[pessoaEsperada].toLowerCase()}.`;
        }
    }
    if (!conferido.valido) {
        return `Este ${conferido.tipo} não é válido — confira os dígitos.`;
    }
    return null;
}
//# sourceMappingURL=documentos-brasileiros.js.map